import { createTheme, Theme } from "@mui/material";
import { scrollBarStyles } from "./scroll-bar";

// Extend the Palette interface to include custom colors
declare module "@mui/material/styles" {
  interface Palette {
    freeTable: Palette["primary"];
    occupiedTable: Palette["primary"];
    color3: Palette["primary"];
    color4: Palette["primary"];
    color5: Palette["primary"];
    color6: Palette["primary"];
    color7: Palette["primary"];
    color8: Palette["primary"];
    color9: Palette["primary"];
    color10: Palette["primary"];
    color11: Palette["primary"];
    color12: Palette["primary"];
    color13: Palette["primary"];
    color14: Palette["primary"];
    color15: Palette["primary"];
    CommonWhite: Palette["primary"];
    CommonBlack: Palette["primary"];
    available: Palette["primary"];
    occupied: Palette["primary"];
    offline: Palette["primary"];
  }

  interface PaletteOptions {
    freeTable?: PaletteOptions["primary"];
    occupiedTable?: PaletteOptions["primary"];
    color3?: PaletteOptions["primary"];
    color4?: PaletteOptions["primary"];
    color5?: PaletteOptions["primary"];
    color6?: PaletteOptions["primary"];
    color7?: PaletteOptions["primary"];
    color8?: PaletteOptions["primary"];
    color9?: PaletteOptions["primary"];
    color10?: PaletteOptions["primary"];
    color11?: PaletteOptions["primary"];
    color12?: PaletteOptions["primary"];
    color13?: PaletteOptions["primary"];
    color14?: PaletteOptions["primary"];
    color15?: PaletteOptions["primary"];
    CommonWhite: PaletteOptions["primary"];
    CommonBlack: PaletteOptions["primary"];
    available: PaletteOptions["primary"];
    occupied: PaletteOptions["primary"];
    offline: PaletteOptions["primary"];
  }
}

// Define the theme configuration
export const lightTheme: Theme = createTheme({
  typography: {
    fontFamily: ["Lato", "Arial", "sans-serif"].join(","),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          ...scrollBarStyles,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          "&.MuiTableCell-root.MuiTableCell-head": {
            backgroundColor: "#F7FAFC", // Soft gray for table header
            color: "#2D3748", // Charcoal for text
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#FFD653', // Match your primary.main or customize
          color: '#1E1E1E',       // Text color
          fontWeight: 600,
          '&:hover': {
            borderColor: '#F2C94C',
            backgroundColor: '#FFF9E5',
          },
          '&.Mui-disabled': {
            borderColor: '#E0E0E0',
            color: '#BDBDBD',
          }
        }
      }
    }
    
  },
  palette: {
    mode: "light", // Enable light mode
    freeTable: { main: "#27AE60" }, // Coral Red
    occupiedTable: { main: "#FF6F61" }, // Teal Blue
    color3: { main: "#27AE60" }, // Emerald Green
    color4: { main: "#F2994A" }, // Vibrant Orange
    color5: { main: "#9B51E0" }, // Violet
    color6: { main: "#56CCF2" }, // Aqua Blue
    color7: { main: "#BB6BD9" }, // Light Purple
    color8: { main: "#EAF4F4" }, // Soft Mint
    color9: { main: "#F2C94C" }, // Yellow
    color10: { main: "#EB5757" }, // Warm Red
    color11: { main: "#F9F7F7" }, // Pearl White
    color12: { main: "#FFFFFF" }, // Pure White
    color13: { main: "#FAFAFA" }, // Off White
    color14: { main: "#ECEFF1" }, // Light Gray
    color15: { main: "#B0BEC5" }, // Soft Blue Gray
    CommonWhite: { main: "#FFFFFF" },
    CommonBlack: { main: "#000000" },
    available: { main: "#27AE60" }, // Emerald for available
    occupied: { main: "#F2C94C" }, // Yellow for occupied
    offline: { main: "#B0BEC5" }, // Soft Gray for offline
    primary: {
      main: "#FFD653", // Teal (New Primary)
      contrastText: "#1E1E1E", // White for contrast
      light: "#FAFAF8"
    },
    secondary: {
      main: "#FF6F61", // Coral Red
      contrastText: "#FFFFFF", // White for contrast
    },
    error: {
      main: "#EB5757", // Warm Red
      contrastText: "#FFFFFF", // White for contrast
    },
    success: {
      main: "#27AE60", // Emerald Green
      contrastText: "#FFFFFF", // White for contrast
    },
    warning: {
      main: "#F2994A", // Vibrant Orange
      contrastText: "#000000", // Black for contrast
    },
    background: {
      default: "#FDFEFF", // Soft gray for the default background
      paper: "#FDFEFF", // White for paper elements
    },
    text: {
      primary: "#1E1E1E", // Charcoal for primary text
      secondary: "#1E1E1E", // Medium Gray for secondary text
    },
    action: {
      hover: "#E2E8F0", // Light Gray for hover states
      selected: "#B2F5EA", // Soft Mint for selected states
    },
  },
});
